import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

import type { Api } from '~/api/endpoints'
import type { DataService, DataState } from '~/data/service'

interface DataContextValue {
  readonly service: DataService
  readonly api: Api
}

const DataContext = createContext<DataContextValue | null>(null)

export interface DataProviderProps {
  readonly service: DataService
  readonly api: Api
  readonly children: ReactNode
}

/**
 * The service the app is running on, for the few writers that are plain functions rather than
 * components — a helper called from inside a handler, where a hook cannot be. One app runs at
 * a time and the provider is what says which; a test rendering another replaces it.
 */
let running: DataService | null = null

export function DataProvider({ service, api, children }: DataProviderProps) {
  /*
   * Said in an effect rather than in the render: which service is running is nothing the
   * render draws from, and writing it while rendering is a side effect React is entitled to
   * run twice or throw away. It lands before any handler can fire, handlers being what read it.
   */
  useEffect(() => {
    running = service
    return () => {
      if (running === service) {
        running = null
      }
    }
  }, [service])

  /*
   * Everything the launch asked for is asked again whenever the tab comes back to the front,
   * so a set logged on the phone — or a day eaten on it — is here without a reload. A tab left
   * open on a desk all afternoon is the same tab, and the app behind it is stale in exactly
   * the way a backgrounded phone app would be.
   *
   * Both events, because they answer different returns: `visibilitychange` catches a tab
   * switched back to, `focus` catches a window raised over another with the tab already
   * showing. One in flight at a time — a return that raises the window and shows the tab
   * fires both, and that is one return, not two.
   */
  const asking = useRef(false)
  useEffect(() => {
    const ask = () => {
      if (asking.current || document.visibilityState !== 'visible') {
        return
      }
      asking.current = true
      void service.refresh().finally(() => {
        asking.current = false
      })
    }
    document.addEventListener('visibilitychange', ask)
    window.addEventListener('focus', ask)
    return () => {
      document.removeEventListener('visibilitychange', ask)
      window.removeEventListener('focus', ask)
    }
  }, [service])

  return <DataContext.Provider value={{ service, api }}>{children}</DataContext.Provider>
}

/** The running service, or null with no app around — which is nothing to write to. */
export function runningDataService(): DataService | null {
  return running
}

export function useDataService(): DataService {
  const value = useContext(DataContext)
  if (!value) {
    throw new Error('useDataService needs a DataProvider above it')
  }
  return value.service
}

export function useApi(): Api {
  const value = useContext(DataContext)
  if (!value) {
    throw new Error('useApi needs a DataProvider above it')
  }
  return value.api
}

/** Subscribes the component to the data layer's state. */
export function useDataState(): DataState {
  const service = useDataService()
  return useSyncExternalStore(service.subscribe, service.getState)
}
